// src/api/client/axios.ts

/**
 * Axios HTTP Client - FIXED
 *
 * KEY FIX: The response interceptor no longer unwraps response.data.
 *
 * WHY:
 * When interceptor returns response.data, Axios treats THAT as the
 * full response. Then HttpService does response.data again, which
 * accesses YOUR API's `data` property instead of Axios's wrapper.
 *
 * The fix: Interceptors handle cross-cutting concerns (logging, timing,
 * token attachment) but DO NOT transform the response shape.
 * Data unwrapping happens in ONE place: HttpService.
 */

import axios, { type AxiosInstance } from "axios";
import { apiConfig } from "@/config";
import {
  requestInterceptor,
  requestErrorInterceptor,
  responseInterceptor,
  errorResponseInterceptor,
} from "./interceptors";
import { logger } from "../../utils";

const createAxiosClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: apiConfig.baseUrl,
    timeout: apiConfig.timeout,
    headers: apiConfig.headers,
    withCredentials: true,
  });

  // Request: attach token + timing metadata
  client.interceptors.request.use(requestInterceptor, requestErrorInterceptor);

  // Response: logging + error transformation
  client.interceptors.response.use(
    responseInterceptor,
    errorResponseInterceptor,
  );

  logger.info("✅ Axios client initialized");
  return client;
};

export const axiosClient = createAxiosClient();
